import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../components/Modal';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../services/authContext';
import { apiService } from '../services/apiService';

const emptyCompose = { subject: '', content: '', recipientIds: [] };

const Messages = () => {
  const { user }                          = useAuth();
  const [tab, setTab]                     = useState('inbox');
  const [refreshKey, setRefreshKey]       = useState(0);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState('');
  const [isCompose, setIsCompose]         = useState(false);
  const [viewMsg, setViewMsg]             = useState(null);
  const [compose, setCompose]             = useState(emptyCompose);
  const [messages, setMessages]           = useState({ inbox: [], sent: [] });
  const [recipients, setRecipients]       = useState([]);
  const [recipientSearch, setRecipientSearch] = useState('');

  useEffect(() => {
    if (!user?.id) return;
    const load = async () => {
      try {
        setLoading(true); setError('');
        const [inbox, sent, users] = await Promise.all([
          apiService.get(`messages/inbox/${user.id}`),
          apiService.get(`messages/sent/${user.id}`),
          apiService.getAll('users'),
        ]);
        setMessages({ inbox: inbox || [], sent: sent || [] });
        setRecipients((users || []).filter(u => String(u._id) !== String(user.id)));
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load messages.');
      } finally { setLoading(false); }
    };
    load();
  }, [refreshKey, user]);

  const list         = useMemo(() => messages[tab] || [], [messages, tab]);
  const unreadCount  = (messages.inbox || []).filter(m => !m.read).length;
  const filteredRecipients = useMemo(() =>
    recipients.filter(r =>
      `${r.firstName} ${r.lastName} ${r.email} ${r.role}`.toLowerCase().includes(recipientSearch.toLowerCase())
    ), [recipients, recipientSearch]);

  const handleView = async msg => {
    setViewMsg(msg);
    if (tab === 'inbox' && !msg.read) {
      try {
        await apiService.post(`messages/read/${msg._id}/${user.id}`, {});
        setRefreshKey(k => k + 1);
      } catch (_) {}
    }
  };

  const openReply = msg => {
    setViewMsg(null);
    setCompose({
      subject: msg.subject.startsWith('Re:') ? msg.subject : `Re: ${msg.subject}`,
      content: '',
      recipientIds: [String(msg.sender?.id)],
    });
    setIsCompose(true);
  };

  const handleSend = async e => {
    e.preventDefault();
    if (!compose.recipientIds.length) { setError('Select at least one recipient.'); return; }
    try {
      setError('');
      await apiService.create('messages', {
        senderId:     user.id,
        recipientIds: compose.recipientIds.map(Number),
        subject:      compose.subject,
        content:      compose.content,
      });
      setCompose(emptyCompose);
      setIsCompose(false);
      setTab('sent');
      setRefreshKey(k => k + 1);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send message.');
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await apiService.delete('messages', id);
      setViewMsg(null);
      setRefreshKey(k => k + 1);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete message.');
    }
  };

  const toggleRecipient = id => {
    const sid = String(id);
    setCompose(p => ({
      ...p,
      recipientIds: p.recipientIds.includes(sid)
        ? p.recipientIds.filter(r => r !== sid)
        : [...p.recipientIds, sid],
    }));
  };

  if (loading) return <LoadingSpinner message="Loading messages..." />;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Messages</h1>
        <button className="btn btn-primary" onClick={() => { setCompose(emptyCompose); setIsCompose(true); }}>+ Compose</button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="messages-tabs">
        <button className={`tab-button ${tab === 'inbox' ? 'active' : ''}`} onClick={() => setTab('inbox')}>
          Inbox {unreadCount > 0 && `(${unreadCount})`}
        </button>
        <button className={`tab-button ${tab === 'sent' ? 'active' : ''}`} onClick={() => setTab('sent')}>
          Sent
        </button>
      </div>

      <div className="messages-list">
        {list.length === 0 ? (
          <div className="no-data-message">No messages in your {tab}.</div>
        ) : list.map(msg => (
          <div
            key={msg._id}
            className={`message-item ${tab === 'inbox' && !msg.read ? 'unread' : ''}`}
            onClick={() => handleView(msg)}
          >
            <span className="message-sender">
              {tab === 'inbox'
                ? msg.sender?.name
                : `To: ${(msg.recipients || []).map(r => r.name).join(', ')}`}
            </span>
            <span className="message-subject">{msg.subject}</span>
            <span className="message-date">{new Date(msg.createdAt).toLocaleDateString()}</span>
          </div>
        ))}
      </div>

      {/* View message modal */}
      <Modal isOpen={!!viewMsg} onClose={() => setViewMsg(null)} title="Message">
        {viewMsg && (
          <div className="message-view">
            <div className="message-header-info">
              <p><strong>From:</strong> {viewMsg.sender?.name}</p>
              <p><strong>Date:</strong> {new Date(viewMsg.createdAt).toLocaleString()}</p>
              <p><strong>Subject:</strong> {viewMsg.subject}</p>
            </div>
            <div className="message-content" style={{ marginTop: '1rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {viewMsg.content}
            </div>
            <div className="form-actions">
              <button className="btn btn-secondary" onClick={() => setViewMsg(null)}>Close</button>
              {tab === 'inbox' && (
                <button className="btn btn-primary" onClick={() => openReply(viewMsg)}>Reply</button>
              )}
              <button className="btn btn-secondary" style={{ color: '#c62828' }} onClick={() => handleDelete(viewMsg._id)}>Delete</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Compose modal */}
      <Modal isOpen={isCompose} onClose={() => setIsCompose(false)} title="Compose Message">
        <form onSubmit={handleSend} className="form">
          <div className="form-group">
            <label>Search Recipients</label>
            <input
              placeholder="Search by name, email or role..."
              value={recipientSearch}
              onChange={e => setRecipientSearch(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Recipients ({compose.recipientIds.length} selected)</label>
            <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 6, padding: '0.5rem' }}>
              {filteredRecipients.length === 0 ? (
                <div style={{ color: 'var(--text-light)', fontSize: '0.85rem', padding: '0.5rem' }}>No users found.</div>
              ) : filteredRecipients.map(r => (
                <label key={r._id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0.25rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={compose.recipientIds.includes(String(r._id))}
                    onChange={() => toggleRecipient(r._id)}
                  />
                  <span>{r.firstName} {r.lastName}</span>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>({r.role})</span>
                </label>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label>Subject</label>
            <input value={compose.subject} onChange={e => setCompose(p => ({ ...p, subject: e.target.value }))} required />
          </div>
          <div className="form-group">
            <label>Message</label>
            <textarea rows={5} value={compose.content} onChange={e => setCompose(p => ({ ...p, content: e.target.value }))} required style={{ resize: 'vertical' }} />
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setIsCompose(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">Send</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Messages;
